import {useStripe} from '@stripe/react-stripe-js'
import {
  CreatePaymentMethodData,
  StripeCardNumberElement,
} from '@stripe/stripe-js'
import {usePurchase} from '../../PurchaseContext'
import React, {useState} from 'react'
import styles from './PurchaseForm.module.css'

export interface PurchaseFormData {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  billing_address?: {
    address_1: string
    address_2?: string
    country_id: string
    state_id: string
    city: string
    zip_postal: string
  }
  shipping_address?: {
    address_1: string
    address_2?: string
    country_id: string
    state_id: string
    city: string
    zip_postal: string
  }
  payment_method_id: string | null
}

interface PurchaseFormProviderProps {
  children: JSX.Element
}

interface PurchaseFormContextProps {
  useSameAddressForShipping: boolean
  setUseSameAddressForShipping: (sameAddress: boolean) => void
  setCardElement: (element: StripeCardNumberElement) => void
  firstName: string
  setFirstName: (value: string) => void
  lastName: string
  setLastName: (value: string) => void
  email: string
  setEmail: (value: string) => void
  phoneNumber: string
  setPhoneNumber: (value: string) => void
  billingCountryId: number
  setBillingCountryId: (value: number) => void
  billingStateId: number
  setBillingStateId: (value: number) => void
  billingCity: string
  setBillingCity: (value: string) => void
  billingAddress1: string
  setBillingAddress1: (value: string) => void
  billingAddress2: string
  setBillingAddress2: (value: string) => void
  billingZipPostal: string
  setBillingZipPostal: (value: string) => void
  shippingCountryId: number
  setShippingCountryId: (value: number) => void
  shippingStateId: number
  setShippingStateId: (value: number) => void
  shippingCity: string
  setShippingCity: (value: string) => void
  shippingAddress1: string
  setShippingAddress1: (value: string) => void
  shippingAddress2: string
  setShippingAddress2: (value: string) => void
  shippingZipPostal: string
  setShippingZipPostal: (value: string) => void
}

const PurchaseFormContext = React.createContext<
  PurchaseFormContextProps | undefined
>(undefined)

export default function PurchaseFormProvider(props: PurchaseFormProviderProps) {
  const {children} = props
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [billingCountryId, setBillingCountryId] = useState(0)
  const [billingStateId, setBillingStateId] = useState(0)
  const [billingAddress1, setBillingAddress1] = useState('')
  const [billingAddress2, setBillingAddress2] = useState('')
  const [billingCity, setBillingCity] = useState('')
  const [billingZipPostal, setBillingZipPostal] = useState('')
  const [shippingCountryId, setShippingCountryId] = useState(0)
  const [shippingStateId, setShippingStateId] = useState(0)
  const [shippingAddress1, setShippingAddress1] = useState('')
  const [shippingAddress2, setShippingAddress2] = useState('')
  const [shippingCity, setShippingCity] = useState('')
  const [shippingZipPostal, setShippingZipPostal] = useState('')

  const stripe = useStripe()

  const [
    cardElement,
    setCardElement,
  ] = useState<StripeCardNumberElement | null>(null)

  const {selectedTicket, submit} = usePurchase()

  const [useSameAddressForShipping, setUseSameAddressForShipping] = useState(
    true,
  )

  const handleSubmit = async () => {
    const data: Omit<PurchaseFormData, 'payment_method_id'> = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: phoneNumber,
    }

    if (billingAddress1) {
      data.billing_address = {
        address_1: billingAddress1,
        address_2: billingAddress2,
        country_id: String(billingCountryId),
        state_id: String(billingStateId),
        city: billingCity,
        zip_postal: billingZipPostal,
      }
    }

    if (shippingAddress1) {
      data.shipping_address = {
        address_1: shippingAddress1,
        address_2: shippingAddress2,
        country_id: String(shippingCountryId),
        state_id: String(shippingStateId),
        city: shippingCity,
        zip_postal: shippingZipPostal,
      }
    }

    if (!selectedTicket) {
      return
    }

    // Submit free tickets immediately without registering a card with
    // Stripe
    if (selectedTicket.price === 0) {
      submit({
        payment_method_id: null,
        ...data,
      })

      return
    }

    if (!stripe || !cardElement) {
      return
    }

    const paymentMethodData: CreatePaymentMethodData = {
      type: 'card',
      card: cardElement,
      billing_details: {
        name: `${data.first_name} ${data.last_name}`,
        email: data.email,
        phone: data.phone_number,
      },
    }

    const {paymentMethod} = await stripe.createPaymentMethod(paymentMethodData)

    submit({
      payment_method_id: paymentMethod?.id ?? null,
      ...data,
    })
  }

  return (
    <PurchaseFormContext.Provider
      value={{
        useSameAddressForShipping,
        setUseSameAddressForShipping,
        setCardElement,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        email,
        setEmail,
        phoneNumber,
        setPhoneNumber,
        billingCountryId,
        setBillingCountryId,
        billingStateId,
        setBillingStateId,
        billingAddress1,
        setBillingAddress1,
        billingAddress2,
        setBillingAddress2,
        billingCity,
        setBillingCity,
        billingZipPostal,
        setBillingZipPostal,
        shippingCountryId,
        setShippingCountryId,
        shippingStateId,
        setShippingStateId,
        shippingAddress1,
        setShippingAddress1,
        shippingAddress2,
        setShippingAddress2,
        shippingCity,
        setShippingCity,
        shippingZipPostal,
        setShippingZipPostal,
      }}
    >
      <form
        className={styles.purchaseForm}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        {children}
      </form>
    </PurchaseFormContext.Provider>
  )
}

export function usePurchaseForm() {
  const context = React.useContext(PurchaseFormContext)
  if (context === undefined) {
    throw new Error(
      'usePurchaseForm must be used within a PurchaseFormProvider',
    )
  }

  return context
}
